import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../components/shared/DashboardLayout';
import API from '../utils/api';

const ReportsIndexPage = () => {
  const [children, setChildren] = useState([]);
  const [previews, setPreviews] = useState({});   // { [childId]: stats }
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    API.get('/children').then(async res => {
      const kids = res.data.children || [];
      setChildren(kids);
      // Fetch lightweight stats for each child in parallel
      const results = await Promise.allSettled(
        kids.map(c => API.get(`/children/${c._id}/stats`))
      );
      const map = {};
      results.forEach((r, i) => {
        if (r.status === 'fulfilled') {
          map[kids[i]._id] = r.value.data.stats;
        }
      });
      setPreviews(map);
    }).catch(err => setLoadError(err.response?.data?.message || 'Could not load reports. Please try again.')).finally(() => setLoading(false));
  }, []);

  return (
    <DashboardLayout title="Reading Reports">
      {/* Header */}
      <div className="rounded-4 p-4 mb-4 d-flex align-items-center justify-content-between flex-wrap gap-3"
        style={{ background: 'var(--purple-section)', border: '1px solid var(--border-purple2)' }}>
        <div>
          <h4 className="fw-bold mb-1" style={{ color: 'var(--text-main)', fontFamily: "'Fredoka One',cursive" }}>
            📊 Reading Reports
          </h4>
          <p className="mb-0 small" style={{ color: '#6f42c1' }}>
            See how many stories each child has read, their favourite genre, most-read story, and monthly activity.
          </p>
        </div>
        <Link to="/dashboard/children" className="btn btn-outline-secondary rounded-pill px-4 fw-semibold" style={{ fontSize: '13px' }}>
          <i className="bi bi-people-fill me-1"></i>Manage Profiles
        </Link>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border" style={{ color: '#6f42c1', width: '3rem', height: '3rem' }}></div>
          <p className="text-muted mt-3">Loading reports…</p>
        </div>
      ) : loadError ? (
        <div className="text-center py-5">
          <div style={{ fontSize: '64px', marginBottom: '16px' }}>⚠️</div>
          <h5 className="fw-bold mb-2" style={{ color: 'var(--text-main)' }}>Couldn't load your reports</h5>
          <p className="text-muted mb-4">{loadError}</p>
          <button className="btn btn-dream rounded-pill px-5" onClick={() => window.location.reload()}>Try Again</button>
        </div>
      ) : children.length === 0 ? (
        <div className="text-center py-5">
          <div style={{ fontSize: '64px', marginBottom: '16px' }}>👶</div>
          <h5 className="fw-bold mb-2" style={{ color: 'var(--text-main)' }}>No child profiles yet</h5>
          <p className="text-muted mb-4">Create a profile to start tracking reading progress.</p>
          <Link to="/dashboard/children" className="btn btn-dream rounded-pill px-5">Create a Profile</Link>
        </div>
      ) : (
        <div className="row g-4">
          {children.map(child => {
            const s = previews[child._id];
            const favoriteGenre = s?.favoriteGenre;
            const GENRE_ICONS = {
              magical: '✨', adventure: '🗺️', adventurous: '🗺️', funny: '😄',
              calm: '🌙', educational: '📚', cozy: '🔥', exciting: '⚡',
              spooky: '👻', fantasy: '🧙', mystery: '🔍', nature: '🌿',
              science: '🔬', bedtime: '🛌',
            };
            const genreIcon = GENRE_ICONS[favoriteGenre?.toLowerCase()] || '📖';

            // Current month stats
            const currentMonth = s?.monthlyReport?.slice(-1)[0];

            return (
              <div className="col-md-6 col-lg-4" key={child._id}>
                <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '20px', overflow: 'hidden' }}>
                  <div style={{ height: '5px', background: 'linear-gradient(90deg,#6f42c1,#9b6fe8,#f9a8d4)' }}></div>
                  <div className="card-body p-4">
                    {/* Child avatar + name */}
                    <div className="d-flex align-items-center gap-3 mb-4">
                      <div className="d-flex align-items-center justify-content-center rounded-circle"
                        style={{ width: '56px', height: '56px', background: 'var(--purple-section)', fontSize: '26px', flexShrink: 0 }}>
                        {child.avatar}
                      </div>
                      <div>
                        <div className="fw-bold" style={{ color: 'var(--text-main)', fontSize: '16px' }}>{child.name}</div>
                        <div className="text-muted small">Age {child.age}</div>
                      </div>
                    </div>

                    {/* Mini stats grid */}
                    {s ? (
                      <>
                        <div className="row g-2 mb-3">
                          <div className="col-6">
                            <div className="rounded-3 p-3 text-center" style={{ background: 'var(--header-bg)', border: '1px solid var(--border-purple)' }}>
                              <div className="fw-bold" style={{ color: '#6f42c1', fontSize: '24px', lineHeight: 1 }}>{s.totalStories}</div>
                              <div className="text-muted" style={{ fontSize: '11px', marginTop: '3px' }}>Stories</div>
                            </div>
                          </div>
                          <div className="col-6">
                            <div className="rounded-3 p-3 text-center" style={{ background: '#e0f7fa', border: '1px solid #b2ebf2' }}>
                              <div className="fw-bold" style={{ color: '#0891b2', fontSize: '24px', lineHeight: 1 }}>{s.totalReads}</div>
                              <div className="text-muted" style={{ fontSize: '11px', marginTop: '3px' }}>Total Reads</div>
                            </div>
                          </div>
                        </div>

                        {/* Favourite genre */}
                        {favoriteGenre && (
                          <div className="mb-3 p-2 rounded-3 d-flex align-items-center gap-2"
                            style={{ background: '#faf5ff', border: '1px solid #e8d5ff' }}>
                            <span style={{ fontSize: '18px' }}>{genreIcon}</span>
                            <div>
                              <div style={{ fontSize: '11px', color: 'var(--text-soft)' }}>Favourite genre</div>
                              <div className="fw-semibold" style={{ color: 'var(--text-main)', fontSize: '13px' }}>
                                {favoriteGenre.charAt(0).toUpperCase() + favoriteGenre.slice(1)}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* This month */}
                        {currentMonth && (currentMonth.storiesGenerated > 0 || currentMonth.storiesRead > 0) && (
                          <div className="mb-3 p-2 rounded-3 d-flex align-items-center justify-content-between"
                            style={{ background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                            <div style={{ fontSize: '12px', color: '#15803d', fontWeight: 500 }}>
                              <i className="bi bi-calendar-check me-1"></i>This month
                            </div>
                            <div className="d-flex gap-3">
                              <span style={{ fontSize: '12px', color: '#6f42c1', fontWeight: 600 }}>
                                {currentMonth.storiesGenerated} generated
                              </span>
                              <span style={{ fontSize: '12px', color: '#0891b2', fontWeight: 600 }}>
                                {currentMonth.storiesRead} read
                              </span>
                            </div>
                          </div>
                        )}

                        {/* Most-read story */}
                        {s.mostReadStory && (
                          <div className="mb-3" style={{ fontSize: '12px', color: 'var(--text-muted2)' }}>
                            <i className="bi bi-trophy-fill me-1" style={{ color: '#f59e0b' }}></i>
                            Most read: <span className="fw-semibold" style={{ color: 'var(--text-main)' }}>
                              {s.mostReadStory.title.length > 40
                                ? s.mostReadStory.title.substring(0, 40) + '…'
                                : s.mostReadStory.title}
                            </span>
                            <span className="text-muted"> ({s.mostReadStory.readCount}×)</span>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="text-center py-3">
                        <div className="spinner-border spinner-border-sm" style={{ color: '#6f42c1' }}></div>
                      </div>
                    )}

                    {/* CTA */}
                    <Link to={`/dashboard/children/${child._id}/report`}
                      className="btn btn-dream rounded-pill w-100 fw-semibold"
                      style={{ fontSize: '13px' }}>
                      <i className="bi bi-bar-chart-fill me-2"></i>Full Reading Report
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </DashboardLayout>
  );
};

export default ReportsIndexPage;
